import React from 'react';
import PropTypes from 'prop-types';
import { InfoWidget } from '@ds/react-components';
import { List as ListLoader } from 'react-content-loader';
import { Typography, Grid, Tooltip, withStyles } from '@material-ui/core';
import moment from 'moment';
import { numberToCurrency, isNullOrUndefined } from 'utils/misc';
import { ReactComponent as InfoCircle } from 'assets/icons/info-circle.svg';

const styles = (theme) => ({
  root: {
    '& h4': {
      fontWeight: 'bold',
      margin: '5px 0',
    },
  },
  error: {
    color: theme.palette.xpl.text.warning,
    fontWeight: 'normal',
  },
  smallText: {
    color: theme.palette.xpl.text.primary,
  },
  headings: {
    color: theme.palette.xpl.text.secondary,
    paddingBottom: '5px',
  },
  data: {
    fontWeight: 'normal',
    paddingBottom: '10px',
  },
  overdueInfoIconContainer: {
    marginLeft: '4px',
  },
  overdueInfoIcon: {
    width: '16px',
    height: '16px',
    verticalAlign: 'middle',
    color: theme.palette.xpl.secondary.default,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  tooltip: {
    backgroundColor: theme.palette.xpl.foreground,
    fontSize: '12px',
    borderRadius: '6px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.palette.xpl.stoke,
    color: theme.palette.common.black,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
    padding: '11px 15px',
  },
  tooltipArrow: {
    color: theme.palette.common.white,
    '&::before': {
      border: `1px solid ${theme.palette.xpl.stoke}`,
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
    },
  },
  overdueBreakdownContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    fontSize: '12px',
    lineHeight: '20px',
  },
  overdueBreakdownItem: {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'space-between',
  },
  overdueBreakdownDivider: {
    borderTop: `1px solid ${theme.palette.xpl.stoke}`,
    margin: '6px 0 5px 0',
    width: '100%',
  },
  boldCurrency: {
    fontWeight: 'bold',
    marginLeft: '43px',
  },
  loader: {
    height: 150,
    width: '100%',
  },
});

const PaymentSummaryPreview = (props) => {
  const { classes, customerDetails, localisation } = props;
  const {
    startDate,
    finishDate,
    numOfTerm,
    overdueAmount,
    membershipDue,
    catchUp,
    catchUpUntil,
    balanceToSettle,
    termType,
    defaultFeeDue,
  } = customerDetails;

  const { dateFormat } = localisation.locales;
  const startDateStr = startDate && moment(startDate).format(dateFormat);
  const finishDateStr = finishDate && moment(finishDate).format(dateFormat);

  const renderOverdueBreakDown = (
    overdueAmount,
    membershipDue,
    defaultFeeDue
  ) => {
    const dsFeeDue = overdueAmount - membershipDue - defaultFeeDue;
    return (
      <div className={classes.overdueBreakdownContainer}>
        <div className={classes.overdueBreakdownItem}>
          <Typography align="left" variant={'h6'}>
            {'Payments'}
          </Typography>
          <Typography
            className={classes.boldCurrency}
            align="right"
            variant={'h6'}>
            {numberToCurrency(membershipDue, localisation)}
          </Typography>
        </div>
        <div className={classes.overdueBreakdownItem}>
          <Typography align="left" variant={'h6'}>
            {'Default Fee'}
          </Typography>
          <Typography
            className={classes.boldCurrency}
            align="right"
            variant={'h6'}>
            {numberToCurrency(defaultFeeDue, localisation)}
          </Typography>
        </div>
        <div className={classes.overdueBreakdownItem}>
          <Typography align="left" variant={'h6'}>
            {'Other fees'}
          </Typography>
          <Typography
            className={classes.boldCurrency}
            align="right"
            variant={'h6'}>
            {numberToCurrency(dsFeeDue, localisation)}
          </Typography>
        </div>
        <div className={classes.overdueBreakdownItem}>
          <div className={classes.overdueBreakdownDivider} />
        </div>
        <div className={classes.overdueBreakdownItem}>
          <Typography align="left" variant={'h6'}>
            {'Total'}
          </Typography>
          <Typography
            className={classes.boldCurrency}
            align={'right'}
            variant={'h6'}>
            {numberToCurrency(overdueAmount, localisation)}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <InfoWidget
      title="Payment Summary"
      contentLoader={<ListLoader className={classes.loader} />}
      isLoading={!customerDetails.adfitNo || balanceToSettle == null}
      className={classes.root}>
      <Grid container spacing={2}>
        <Grid item lg={4}>
          <Typography className={classes.headings}>
            Contract start date
          </Typography>
          <Typography variant="h4" className={classes.data}>
            {startDateStr}
          </Typography>
        </Grid>
        <Grid item lg={4}>
          <Typography className={classes.headings}>
            {'Contract finish date'}
          </Typography>
          <Typography variant="h4" className={classes.data}>
            {customerDetails.isActive ? 'Ongoing' : finishDateStr}
          </Typography>
        </Grid>
        <Grid item lg={4}>
          <Typography className={classes.headings}>Minimum term</Typography>
          <Typography variant="h4" className={classes.data}>
            {numOfTerm} {localisation.prompts.termType[termType]}
          </Typography>
        </Grid>
        <Grid item lg={4}>
          <Typography className={classes.headings}>
            Balance to settle
          </Typography>
          <Typography
            variant="h4"
            className={classes.data}>{`${numberToCurrency(
            balanceToSettle || 0,
            localisation
          )}`}</Typography>
        </Grid>
        {overdueAmount > 0 && (
          <Grid item lg={4}>
            <Typography className={classes.headings}>Overdue</Typography>
            <Grid alignItems="center" container>
              <Grid item>
                <Typography variant="h4" className={classes.error}>
                  {`${numberToCurrency(overdueAmount || 0, localisation)}`}
                </Typography>
              </Grid>
              {!isNullOrUndefined(membershipDue) && (
                <Grid item className={classes.overdueInfoIconContainer}>
                  <Tooltip
                    arrow
                    placement="top"
                    enterTouchDelay={100}
                    leaveTouchDelay={3500}
                    title={renderOverdueBreakDown(
                      overdueAmount,
                      membershipDue,
                      defaultFeeDue
                    )}
                    classes={{
                      tooltip: classes.tooltip,
                      arrow: classes.tooltipArrow,
                    }}>
                    <InfoCircle className={classes.overdueInfoIcon} />
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        {catchUp > 0 && (
          <Grid item lg={4}>
            <Typography className={classes.headings}>
              Catch up payment
            </Typography>
            <Typography
              variant="h4"
              className={classes.error}>{`${numberToCurrency(
              catchUp || 0,
              localisation
            )}`}</Typography>
            <Typography variant="h6" className={classes.smallText}>
              until{' '}
              {catchUpUntil
                ? `${moment(catchUpUntil).format(dateFormat)}`
                : 'fully paid'}
            </Typography>
          </Grid>
        )}
      </Grid>
    </InfoWidget>
  );
};

PaymentSummaryPreview.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(PaymentSummaryPreview);
